import { AttachInternals, Component, ComponentInterface, Element, Event, EventEmitter, h, Host, JSX, Prop, State, Watch } from '@stencil/core';
import { transformTagNames } from '../../../global/transform-tag-name';
import { debounce } from '../../../utils/debounce';
import { InputEvent, InputMode, InputType, ListItemSelection } from '../combobox.types';
import { GreyBoxSize } from '../../grey-box/grey-box.types';

/**
 * @slot label - Text placed within the inner label element (required)
 * @slot actions - One or more wb-input-action elements that are placed at the end of the input (optional)
 * @slot leading-icon - Icon to display at the start of the input (optional)
 */
@Component({
  tag: 'wb-combobox-input',
  shadow: true,
  formAssociated: true,
})
export class ComboboxInputComponent implements ComponentInterface {
  @Element()
  public el: HTMLElement;

  /**
   *  Indicates if combobox overlay is open.
   *  Will be set by parent component.
   *  @internal
   */
  @Prop({ reflect: true })
  public open: boolean = false;

  /**
   * The size of the input
   * Will be set by parent component.
   * @internal
   */
  @Prop({ reflect: true })
  public size?: GreyBoxSize = 'l';

  /** Debounce input change events for given time */
  /** @default 0 */
  @Prop({ reflect: true })
  public debounceTime?: number = 0;

  /** Marks input as successful */
  @Prop({ reflect: true })
  public success?: boolean;

  /** marks control as invalid. automatically set by slotted wb-control-error */
  @Prop({ reflect: true, mutable: true })
  public error?: boolean;

  /**
   * The type of the input.
   * Type date and type password are not supported as combobox input.
   */
  @Prop({ reflect: true })
  public type?: InputType = 'text';

  /** This allows a browser to display an appropriate virtual keyboard. */
  @Prop({ reflect: true })
  public inputmode?: InputMode;

  /** placeholder of the input. */
  @Prop({ reflect: true })
  public placeholder?: string;

  /** label describing to a screen reader the state of a validation */
  @Prop({ reflect: true })
  public readerValidationMessage?: string = '';

  /**
   * Makes the input component disabled.
   * This prevents users from being able to interact with the component, and conveys its inactive state to assistive technologies.
   */
  @Prop({ reflect: true })
  public disabled?: boolean = false;

  /** The name of the input component. */
  @Prop({ reflect: true })
  public name: string;

  /** The value of the input component. */
  @Prop({ reflect: true, mutable: true })
  public value?: string | ListItemSelection;

  /**
   * Determines whether the input is required or not.
   * An input marked as required will be announced as such to users of assistive technology.
   * When using this Prop you need to also set “novalidate” attribute on a form element to prevent browser from displaying its own validation errors.
   */
  @Prop({ reflect: true })
  public required?: boolean = false;

  /** Prevents the value from being editable. */
  @Prop({ reflect: true })
  public readonly?: boolean = false;

  /** If present, indicates that the input should automatically have focus when the page has finished loading */
  @Prop({ reflect: true, attribute: 'autofocus' })
  public autoFocus?: boolean = false; // stencil didn't like when the property had the name of `autofocus`

  /**
   * If combobox is of variant "multi" a suffix can be provided which is shown after the selection count.
   * @example selectionCountSuffix="countries selected" will be shown in the input as e.g. "10 countries selected" when the selection is closed
   */
  @Prop({ reflect: true })
  public selectionCountSuffix?: string = 'values selected';

  /**
   * Shows selected value or number of selected values if type is multi select.
   * @internal
   */
  @Prop()
  public selectedValue: ListItemSelection;

  /** aria-label for button responsible to do an action */
  @Prop({ reflect: true, mutable: true })
  public buttonActionLabel: string;

  /** Emitted when the input value changes */
  @Event()
  public wbcomboboxinputchange: EventEmitter<InputEvent>;

  /** Emitted when the input fires an input event */
  @Event()
  public wbcomboboxinput: EventEmitter<InputEvent>;

  /** Emitted when the input gets focus */
  @Event()
  public wbcomboboxinputfocus: EventEmitter<void>;

  /** Emitted when the input blurs */
  @Event()
  public wbcomboboxinputblur: EventEmitter<void>;

  /** Emitted when a click inside the input happens */
  @Event()
  public wbcomboboxinputclick: EventEmitter<void>;

  @State() private hasLeadingIcon = false;

  @Watch('value')
  public onValueChange(newValue: string) {
    this.inputEl.value = newValue;
    this.setFormAssociation();
  }

  @Watch('selectedValue')
  public onSelectedValueChange(newVal: ListItemSelection, oldVal: ListItemSelection): void {
    // If a value is deselected the new val will be undefined
    if (!newVal) {
      return;
    }

    const shouldSetSelectedValueAsInputValue = !this.open && !Array.isArray(newVal) && JSON.stringify(newVal) !== JSON.stringify(oldVal);

    if (shouldSetSelectedValueAsInputValue) {
      this.value = newVal.label;
    }

    this.handleVisibleValue(true);
  }

  /** To allow the input to be used as a native date input https://stenciljs.com/docs/form-associated */
  @AttachInternals() internals: ElementInternals;

  private TagNames = transformTagNames(this, 'wb-combobox-input', ['wb-input']);
  private inputEl!: HTMLWbInputElement;
  private debouncedHandleInput: (...args) => void;
  private debouncedHandleChange: (...args) => void;

  connectedCallback() {
    this.handleLeadingIconChange();
    // Debounced functions have to be initialized here, otherwise this.debounceTime isn't initialized yet
    this.debouncedHandleInput = debounce(this.emitChangedInputVal.bind(this), this.debounceTime);
    this.debouncedHandleChange = debounce(this.emitChangeEvent.bind(this), this.debounceTime);
  }

  public componentDidLoad(): void {
    this.handleVisibleValue(true);
  }

  /** Set form association values so browser will consider this input in constrain validation https://stenciljs.com/docs/form-associated https://developer.mozilla.org/en-US/docs/Web/HTML/Constraint_validation */
  private setFormAssociation(): void {
    if (!Array.isArray(this.selectedValue)) {
      this.internals.setFormValue(this.selectedValue.value);
    }

    this.internals.setValidity(this.inputEl.validity, this.inputEl?.validationMessage, this.inputEl);
  }

  private handleClick = (): void => {
    if (this.disabled) {
      return;
    }

    // Toggle visible value for input based on whether it's open + adding focus to input
    if (!this.open) {
      this.inputEl.shadowRoot.querySelector('input').focus();
      this.handleVisibleValue(false);
    } else {
      this.handleVisibleValue(true);
    }

    this.wbcomboboxinputclick.emit();
  };

  private handleBlur = (): void => {
    this.handleVisibleValue(true);
    this.wbcomboboxinputblur.emit();
  };

  private handleFocus = (): void => {
    this.wbcomboboxinputfocus.emit();
  };

  private emitChangeEvent = (event: Event): void => {
    const target = event.target as HTMLInputElement;
    this.wbcomboboxinputchange.emit(target);
  };

  private emitChangedInputVal = (event: CustomEvent<InputEvent>): void => {
    this.value = event.detail.value;
    this.wbcomboboxinput.emit({ name: this.name, value: this.value });
  };

  private bindRef = (el) => {
    this.inputEl = el;
  };

  private getValueLabel = (): string => {
    if (!this.value) {
      return '';
    }

    if (!Array.isArray(this.value) && typeof this.value !== 'string') {
      return this.value.label;
    }

    return this.value as string;
  };

  private getSelectedValueLabel = (): string => {
    if (!this.selectedValue || (Array.isArray(this.selectedValue) && !this.selectedValue.length)) {
      return '';
    }

    if (Array.isArray(this.selectedValue)) {
      return `${this.selectedValue.length} ${this.selectionCountSuffix}`;
    }

    return this.selectedValue.label;
  };

  // Returns the label of the selected value if available, otherwise returns the default value label
  private getValue = (): string => {
    return this.selectedValue ? this.getSelectedValueLabel() : this.getValueLabel();
  }

  // Returns the current value or falls back to the placeholder text if no value exists
  private getPlaceholder = (): string => {
    return this.getValue() || this.placeholder;
  }

  // Shows the current value if it should be visible, otherwise clears it
  private handleVisibleValue = (visible: boolean): void => {
    if (this.inputEl) {
      this.inputEl.value = visible ? this.getValue() : '';
    }
  }

  private handleLeadingIconChange = (): void => {
    this.hasLeadingIcon = Boolean(this.el.querySelector('[slot="leading-icon"]'));
  };

  private handleInputEvent = (event: CustomEvent<InputEvent>) => {
    // If propagation not stopped here, wbinput would be emitted immediatly
    // even if it's handled inside of debounce callback function
    event.stopPropagation();
    this.debouncedHandleInput(event);
  };

  private handleChangeEvent = (event: Event) => {
    // If propagation not stopped here, wbchange would be emitted immediatly
    // even if it's handled inside of debounce callback function
    event.stopPropagation();
    this.debouncedHandleChange(event);
  };

  public render(): JSX.Element {
    const { WbInput } = this.TagNames;

    return (
      <Host class="wb-combobox-input">
        <WbInput //
          data-testid="wb-combobox-input"
          ref={this.bindRef}
          size={this.size}
          success={this.success}
          error={this.error}
          type={this.type}
          inputmode={this.inputmode}
          placeholder={this.getPlaceholder()}
          readerValidationMessage={this.readerValidationMessage}
          disabled={this.disabled}
          name={this.name}
          required={this.required}
          readonly={this.readonly}
          hasCaretIcon={!this.readonly}
          open={this.open}
          autoFocus={this.autoFocus}
          autocomplete="off"
          specialActionLabel={this.buttonActionLabel}
          onBlur={this.handleBlur}
          onWbchange={this.handleChangeEvent}
          onFocus={this.handleFocus}
          onWbinput={this.handleInputEvent}
          onClick={this.handleClick}
        >
          {/*Offer same slots as WbInput for the user*/}
          {this.hasLeadingIcon ? <slot slot="leading-icon" name="leading-icon" onSlotchange={this.handleLeadingIconChange} /> : null}
          <slot slot="label" name="label" />
          <slot slot="actions" name="actions" />
        </WbInput>
      </Host>
    );
  }
}
